
import {
  Drawer,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Divider,
  Fab,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Chip,
} from "@mui/material";
import {
  AddTask,
  Delete as DeleteIcon,
  PendingActions,
  CheckCircleOutline,
  Lock,
  Public,
  PublicOff,
} from "@mui/icons-material";
import { BatchTask } from "../util/Batch";
import { Item } from "../util/Item";
import { Util } from "../util/Util";
import { mapTasksWithActivation } from "../util/taskActivation";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Z_INDEX } from "../styles/zIndex";

interface Props<T extends Item, U extends BatchTask<T>> {
  tasks: U[];
  setTasks: Dispatch<SetStateAction<U[]>>;
  addNewTask: () => void;
  tasksOpen: boolean;
  onCallback?: () => void;
  currentUserId?: number;
  onTogglePublic?: (task: U) => void;
  onDeleteTask?: (id: string) => void;
}

const TaskPane = <T extends Item, U extends BatchTask<T>>({
  tasks,
  setTasks,
  addNewTask,
  tasksOpen,
  onCallback,
  currentUserId,
  onTogglePublic,
  onDeleteTask,
}: Props<T, U>) => {
  const { t } = useTranslation();
  const activeTask = tasks?.find((task) => task.active);

  const onTaskSelected = (selectedTask: BatchTask<T>) => {
    setTasks((prevTasks) =>
      prevTasks
        ? mapTasksWithActivation(prevTasks, (task) => task.id === selectedTask.id)
        : []
    );
  };

  const handleDelete = (id: string) => {
    if (onDeleteTask) {
      onDeleteTask(id);
      return;
    }
    setTasks((prevTasks) => prevTasks.filter((curTask) => curTask.id !== id));
  };

  return (
    <Drawer
      anchor="right"
      open={tasksOpen}
      onClose={onCallback}
      sx={{
        zIndex: Z_INDEX.taskPane,
        "& .MuiBackdrop-root": {
          zIndex: Z_INDEX.taskPane - 1,
        },
      }}
      PaperProps={{
        sx: {
          zIndex: Z_INDEX.taskPane,
          borderTopLeftRadius: 12,
          borderBottomLeftRadius: 12,
          borderLeft: "1px solid",
          borderColor: "divider",
          boxShadow: 12,
        },
      }}
    >
      <Box
        sx={{
          width: 400,
          display: "flex",
          flexDirection: "column",
          height: "100%",
          bgcolor: "background.paper",
          p: 2,
          backgroundImage:
            "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 25%)",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography
            variant="h6"
            sx={{
              display: "flex",
              alignItems: "center",
              fontWeight: 700,
              color: "text.primary",
            }}
          >
            <PendingActions sx={{ mr: 1, color: "primary.main" }} /> {t("common:taskPane.openedTasks", { count: tasks.length })}
          </Typography>

          <Button
            variant="contained"
            startIcon={<AddTask />}
            onClick={addNewTask}
            size="small"
            sx={{
              borderRadius: 3,
              fontWeight: 600,
              boxShadow: 2,
              textTransform: "none",
              px: 1.75,
            }}
          >
            {t("common:ui.buttons.add")}
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Task List */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", pr: 1 }}>
          {tasks.length === 0 && (
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              sx={{ mt: 8, fontStyle: "italic" }}
            >
              {t("common:taskPane.noTasks")}
            </Typography>
          )}

          {tasks.map((task) => (
            <Card
              key={task.id}
              elevation={task.id === activeTask?.id ? 6 : 1}
              onClick={() => {
                onTaskSelected(task);
                onCallback?.();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onTaskSelected(task);
                  onCallback?.();
                }
              }}
              role="button"
              tabIndex={0}
              sx={{
                mb: 2,
                cursor: "pointer",
                transition: "all 0.25s ease",
                border: 1,
                borderColor:
                  task.id === activeTask?.id ? "primary.main" : "divider",
                outline: "none",
                borderRadius: 2,
                backgroundColor:
                  task.id === activeTask?.id
                    ? "action.selected"
                    : "background.paper",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: 6,
                },
                "&:focus-visible": {
                  borderColor: "primary.main",
                  boxShadow: 4,
                },
              }}
            >
              <CardContent>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={1}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    fontWeight={500}
                  >
                    {t("common:taskPane.lastUpdated")}
                  </Typography>
                  <Typography variant="body2" color="text.primary" fontWeight={600}>
                    {task.updatedAt.toLocaleString()}
                  </Typography>
                </Stack>

                <Divider sx={{ mb: 1 }} />

                <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
                  {task.items!.slice(0, 3).map((item: T, idx) => (
                    <li key={idx}>
                      <Typography variant="body2">
                        {item.item}: {Util.formatNumber(item.quantity)} ×{" "}
                        {Util.formatNumber(item.price)} ={" "}
                        {Util.formatNumber(item.total)}
                      </Typography>
                    </li>
                  ))}
                  {task.items!.length > 3 && (
                    <li>
                      <Typography variant="body2" color="text.secondary">
                        ...
                      </Typography>
                    </li>
                  )}
                </ul>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  mt={1}
                  sx={{ fontStyle: "italic" }}
                >
                  {t("common:taskPane.notes")}: {task.description || t("common:taskPane.none")}
                </Typography>

                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={1}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t("common:taskPane.created")}: {task.createdAt.toLocaleString()}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Chip
                      size="small"
                      icon={task.is_public ? <Public /> : <PublicOff />}
                      label={task.is_public ? t("common:taskPane.public") : t("common:taskPane.private")}
                      variant="outlined"
                    />
                    {task.locked_by && Number(task.locked_by) !== Number(currentUserId || 0) && (
                      <Chip
                        size="small"
                        icon={<Lock />}
                        color="warning"
                        label={t("common:taskPane.lockedBy", { name: task.locked_by_name || task.locked_by })}
                      />
                    )}
                    {task.id === activeTask?.id && (
                      <Chip
                        icon={<CheckCircleOutline />}
                        label={t('common:status.active')}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Stack>
              </CardContent>

              <CardActions
                sx={{
                  justifyContent: "space-between",
                  px: 2,
                  py: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="success.main"
                >
                  {t("common:taskPane.total")}: {Util.formatNumber(task.total())}
                </Typography>

                <Stack direction="row" spacing={0.5}>
                  {onTogglePublic && Number(task.owner_id || currentUserId) === Number(currentUserId) && (
                    <Tooltip title={task.is_public ? t("common:taskPane.makePrivate") : t("common:taskPane.makePublic")}>
                      <IconButton
                        size="small"
                        aria-label={task.is_public ? t("common:taskPane.makePrivate") : t("common:taskPane.makePublic")}
                        onClick={(e) => {
                          e.stopPropagation();
                          onTogglePublic(task);
                        }}
                      >
                        {task.is_public ? <PublicOff /> : <Public />}
                      </IconButton>
                    </Tooltip>
                  )}
                  {(!task.owner_id || Number(task.owner_id) === Number(currentUserId)) && (
                    <Tooltip title={t("common:taskPane.deleteTask")}>
                      <IconButton
                        size="small"
                        aria-label={t("common:taskPane.deleteTask")}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(task.id);
                        }}
                        sx={{ color: "error.main" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              </CardActions>
            </Card>
          ))}
        </Box>
      </Box>
    </Drawer>
  );
};

export default TaskPane;
